import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus, FaPen } from 'react-icons/fa';
import { FaMapLocationDot, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { Post, updatePostInteraction, updatePostCommentCount, updatePostSpotlight } from '@/redux/slices/dashboardSlice';
import {
  updatePostInteraction as updatePostSliceInteraction,
  updateCommentCount as updatePostSliceCommentCount,
  updateCurrentPostSpotlight as updatePostSliceSpotlight,
} from '@/redux/slices/postSlice';
import { togglePostLike, togglePostDislike, toggleFollow } from '@/redux/thunks/dashboardThunks';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { selectKlyque } from '@/redux/selectors/dashboardSelectors';
import { useSignupGuard } from '@/hooks/useSignupGuard';
import UserActionsMenu from '@/components/UserActionsMenu/UserActionsMenu';
import { CommentList } from '@/components/Post';
import { SpotlightedPost } from '@/components/SpotlightedPost';
import { PostSpotlightButton } from '@/components/PostSpotlightButton';
import { SpotlightSuccessModal } from '@/components/SpotlightSuccessModal';
import SpotlightCountdown from '@/components/SpotlightCountdown/SpotlightCountdown';
import { CreatorRankBadge } from '@/components/CreatorRankBadge';
import PaywallModal from '@/components/Post/PaywallModal';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import { formatRelativeTime } from '@/utils/formatTime';
import { profileLink } from '@/utils/profileLink';
import PostBodyRenderer from '@/components/Post/PostBodyRenderer';
import PostComposer from '@/components/Post/PostComposer';
import HintTooltip from '@/components/HintTooltip';
import api from '@/utils/api';
import { editPost } from '@/redux/thunks/dashboardThunks';
import './PostCard.scss';

interface PostCardProps {
  post: Post;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Don't render the "open post" affordance on the post-detail page —
  // we're already there.
  const isOnPostDetail = location.pathname.startsWith(`/post/${post.id}`);
  const [showComments, setShowComments] = useState(false);
  const currentUserId = useAppSelector((state) => state.auth?.user?.id);
  const [showFollowBtn, setShowFollowBtn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedDuration, setCompletedDuration] = useState(24);
  // Owner-only edit modal (#292). Open state + submit handler wired
  // below in the JSX for the edit button.
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isLocked, setIsLocked] = useState(post.isLocked ?? false);
  const [unlockedBody, setUnlockedBody] = useState<string | null>(null);
  const [unlockedMediaUrl, setUnlockedMediaUrl] = useState<string | null>(null);
  const [viewFee, setViewFee] = useState<string | null>(null);
  const [subscriptionFee, setSubscriptionFee] = useState<string | null>(null);
  const [subscriptionPeriodSeconds, setSubscriptionPeriodSeconds] = useState<number | null>(null);

  // Get following list from Redux state instead of fetching for each post
  const klyque = useAppSelector(selectKlyque);
  const followingUserIds = useMemo(() => klyque.map(user => user.userId), [klyque]);

  const isOwner = currentUserId === post.postedBy.userId;

  // Derive follow status from Klyque list - no API call needed!
  const isFollowingAuthor = useMemo(
    () => followingUserIds.includes(post.postedBy.userId),
    [followingUserIds, post.postedBy.userId]
  );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-unlock on mount was removed: on a profile page with N locked
  // PPV posts, N useEffects each fired `/posts/:id/content`, which does
  // 2-3 on-chain queries per call (hasViewAccess + hasActiveSubscription
  // + hasPaidPPV). The Klever node rate-limits sustained bursts and
  // returned 429s, so most access checks failed and posts stayed locked
  // even when the user had paid — the exact opposite of what "auto-
  // unlock" was trying to achieve.
  //
  // The DB-cached access flag from `getUserPosts` (populated on payment
  // via /confirm-ppv) is now the sole source of truth for the initial
  // lock state. If the DB cache is stale (rare — really only when
  // /confirm-ppv failed silently after a successful on-chain tx),
  // handleUnlockClick below re-checks on-chain when the user actually
  // clicks Unlock — one chain query per click, not N per page load.

  // PostCard renders from three places with different state-management
  // strategies:
  //   - KlyxFeed → dashboardSlice arrays (spotlight/location/klyque)
  //   - /post/[id] → postSlice.currentPost
  //   - UserPostsFeed → local component useState
  // The third case is why the on-card counter used to look broken: the
  // Redux reducers updatePostInteraction / updatePostSliceInteraction
  // walk their own slice arrays and no-op for posts they don't hold,
  // so a post coming from UserPostsFeed's local state never picked up
  // the change. Fix: mirror the interaction in the card's own state.
  // The Redux dispatches still fire so the other two surfaces stay in
  // sync if they happen to render the same post elsewhere.
  const [interaction, setInteraction] = useState({
    liked: post.userInteraction?.liked ?? false,
    disliked: post.userInteraction?.disliked ?? false,
    likes: post.stats.likes,
    dislikes: post.stats.dislikes,
    // Mirrored for the same reason as likes/dislikes: UserPostsFeed
    // renders PostCard with a local-useState `posts` array (KlyxFeed
    // and PostDetail use Redux slices that the existing dispatches
    // already patch). Without this mirror, a new comment on a post
    // rendered through UserPostsFeed shows up in the comment list
    // but the count badge stays stale until a page refresh.
    comments: post.stats.comments,
  });
  // Resync if the prop changes. Keying only on post.id used to mean
  // that when Redux (dashboardSlice.updatePostInteraction or
  // postSlice) pushed new counts for the same post, the local mirror
  // ignored them and the user's like count stayed stale until a
  // refresh (#247). Including the stats fields in deps reseeds on
  // any authoritative count change while still no-op'ing when the
  // values are the same.
  useEffect(() => {
    setInteraction({
      liked: post.userInteraction?.liked ?? false,
      disliked: post.userInteraction?.disliked ?? false,
      likes: post.stats.likes,
      dislikes: post.stats.dislikes,
      comments: post.stats.comments,
    });
  }, [
    post.id,
    post.stats.likes,
    post.stats.dislikes,
    post.stats.comments,
    post.userInteraction?.liked,
    post.userInteraction?.disliked,
  ]);

  const { requireAuth } = useSignupGuard();

  const handleLike = async () => {
    if (!requireAuth('Sign up to like posts')) return;
    const prev = interaction;
    // Optimistic update — flip liked; if currently disliked, the
    // server clears that, so reflect both flips locally.
    const nextLiked = !prev.liked;
    const nextDisliked = nextLiked ? false : prev.disliked;
    setInteraction({
      liked: nextLiked,
      disliked: nextDisliked,
      likes: prev.likes + (nextLiked ? 1 : -1),
      dislikes: prev.dislikes + (prev.disliked && !nextDisliked ? -1 : 0),
      comments: prev.comments,
    });
    try {
      const result = await dispatch(togglePostLike(post.id)).unwrap();
      // Server is source of truth — patch local state from the
      // response. The server now also returns the authoritative
      // likeCount + dislikeCount (#247) so the visible counter on
      // the card matches the DB instead of leaving the optimistic
      // delta in place forever, even when the prop comes back from
      // Redux with the same id and the resync effect wouldn't fire.
      setInteraction((cur) => ({
        liked: result.liked,
        disliked: result.disliked,
        likes: typeof result.likeCount === 'number' ? result.likeCount : cur.likes,
        dislikes: typeof result.dislikeCount === 'number' ? result.dislikeCount : cur.dislikes,
        comments: cur.comments,
      }));
      const payload = { liked: result.liked, disliked: result.disliked };
      dispatch(updatePostInteraction({ postId: post.id, ...payload }));
      dispatch(updatePostSliceInteraction(payload));
    } catch (error) {
      // Rollback on failure
      setInteraction(prev);
      console.error('Failed to toggle like:', error);
    }
  };

  const handleDislike = async () => {
    if (!requireAuth('Sign up to react to posts')) return;
    const prev = interaction;
    const nextDisliked = !prev.disliked;
    const nextLiked = nextDisliked ? false : prev.liked;
    setInteraction({
      liked: nextLiked,
      disliked: nextDisliked,
      likes: prev.likes + (prev.liked && !nextLiked ? -1 : 0),
      dislikes: prev.dislikes + (nextDisliked ? 1 : -1),
      comments: prev.comments,
    });
    try {
      const result = await dispatch(togglePostDislike(post.id)).unwrap();
      setInteraction((cur) => ({
        liked: result.liked,
        disliked: result.disliked,
        likes: typeof result.likeCount === 'number' ? result.likeCount : cur.likes,
        dislikes: typeof result.dislikeCount === 'number' ? result.dislikeCount : cur.dislikes,
        comments: cur.comments,
      }));
      const payload = { liked: result.liked, disliked: result.disliked };
      dispatch(updatePostInteraction({ postId: post.id, ...payload }));
      dispatch(updatePostSliceInteraction(payload));
    } catch (error) {
      setInteraction(prev);
      console.error('Failed to toggle dislike:', error);
    }
  };

  const handleComment = () => {
    setShowComments(!showComments);
  };

  const handleFetchComments = async (postId: string, limit: number, offset: number) => {
    const response = await api.get(`/comments/post/${postId}`, { params: { limit, offset } });
    return response.data;
  };

  const handleCreateComment = async (postId: string, body: string) => {
    const response = await api.post(`/comments/post/${postId}`, { body });
    dispatch(updatePostCommentCount({ postId, increment: 1 }));
    dispatch(updatePostSliceCommentCount(1));
    setInteraction(prev => ({ ...prev, comments: prev.comments + 1 }));
    return response.data;
  };

  const handleUpdateComment = async (commentId: string, body: string) => {
    const response = await api.patch(`/comments/${commentId}`, { body });
    return response.data;
  };

  const handleDeleteComment = async (commentId: string) => {
    await api.delete(`/comments/${commentId}`);
    dispatch(updatePostCommentCount({ postId: post.id, increment: -1 }));
    dispatch(updatePostSliceCommentCount(-1));
    setInteraction(prev => ({ ...prev, comments: Math.max(0, prev.comments - 1) }));
  };

  const handleAvatarClick = () => {
    if (isOwner) {
      navigate(profileLink(post.postedBy.username, 'feed', { ref: post.id }));
    } else if (isMobile) {
      // On mobile, clicking avatar toggles follow button
      setShowFollowBtn(!showFollowBtn);
    }
    // On desktop, clicking avatar does nothing (hover shows follow button, username navigates to profile)
  };

  const handleUsernameClick = () => {
    navigate(profileLink(post.postedBy.username, 'feed', { ref: post.id }));
  };

  const handleFollow = async () => {
    // Snapshot pre-toggle so the toast label matches the transition
    // we just committed to (isFollowingAuthor is a useMemo over Redux
    // state and flips after the dispatch settles).
    const wasFollowing = isFollowingAuthor;
    const displayName = post.postedBy.displayName || post.postedBy.username;

    try {
      await dispatch(toggleFollow({
        targetUserId: post.postedBy.userId,
        user: {
          userId: post.postedBy.userId,
          username: post.postedBy.username,
          displayName: post.postedBy.displayName,
          avatar: post.postedBy.avatar,
        }
      })).unwrap();

      // isFollowingAuthor will automatically update via useMemo when Redux state changes
      toast.success(wasFollowing ? `Unfollowed ${displayName}` : `Following ${displayName}`);

      if (isMobile) setShowFollowBtn(false);
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      toast.error(wasFollowing ? 'Failed to unfollow' : 'Failed to follow');
    }
  };

  // Check if user can spotlight this post (owner or poster)
  const canSpotlight = currentUserId && (
    currentUserId === post.postedBy.userId ||
    currentUserId === post.ownerUserId
  );

  const handleUnlockClick = async () => {
    // Anon users can't buy PPV / subscribe without an account. Prompt
    // signup before doing anything else — no access-check API call,
    // no paywall modal render (both would silently fail for anon).
    // ADR-007 PR 5.
    if (!requireAuth(post.viewType === 'ppv' ? 'Sign up to unlock this post' : 'Sign up to subscribe')) return;

    // First check if user already has on-chain access (DB cache may be stale)
    try {
      const { data } = await api.get(`/posts/${post.id}/content`);
      // Access confirmed — unlock directly without showing the paywall
      setUnlockedBody(data.body);
      setUnlockedMediaUrl(data.mediaUrl || null);
      setIsLocked(false);
      toast.success('Post unlocked!');
      return;
    } catch {
      // 403 or error — proceed to show the paywall
    }

    setShowPaywall(true);
    // Lazily fetch creator profile fees for 'paid' posts
    if (post.viewType === 'paid' && post.profileId && viewFee === null) {
      try {
        const { data } = await api.get(`/blockchain-profiles/${post.profileId}/fees`);
        const fees = data.fees;
        setViewFee(fees?.viewFee && fees.viewFee !== '0' ? fees.viewFee : null);
        setSubscriptionFee(fees?.subscriptionFee && fees.subscriptionFee !== '0' ? fees.subscriptionFee : null);
        setSubscriptionPeriodSeconds(fees?.subscriptionPeriodSeconds ?? null);
      } catch {
        // non-fatal — modal will show with what it has
      }
    }
  };

  const handleUnlocked = async () => {
    // Retry up to 3 times — on-chain confirmation may lag behind the transaction broadcast
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
        const { data } = await api.get(`/posts/${post.id}/content`);
        setUnlockedBody(data.body);
        setUnlockedMediaUrl(data.mediaUrl || null);
        setIsLocked(false);
        setShowPaywall(false);
        toast.success('Post unlocked!');
        return;
      } catch {
        // continue to next attempt
      }
    }
    // All attempts failed — optimistic unlock with preview text only
    setUnlockedBody(post.previewText || '');
    setIsLocked(false);
    setShowPaywall(false);
    toast.info('Post unlocked! Full content may take a moment to appear.');
  };

  return (
    <SpotlightedPost
      isSpotlighted={!!post.spotlight}
      spotlightEndTime={post.spotlight?.endTime}
      showIndicator={false}
      feedType="default"
      className="post-card"
    >
      {/* Post Header */}
      <div className="post-header">
        <div className="post-author">
          <div
            className="author-avatar"
            onMouseEnter={() => !isOwner && !isMobile && setShowFollowBtn(true)}
            onMouseLeave={() => !isMobile && setShowFollowBtn(false)}
            onClick={handleAvatarClick}
          >
            {post.postedBy.avatar ? (
              <img src={getMediaUrl(post.postedBy.avatar) || undefined} alt={post.postedBy.username} />
            ) : (
              <div className="avatar-placeholder">
                {nameInitial({ displayName: post.postedBy.displayName, username: post.postedBy.username })}
              </div>
            )}
            {!isOwner && showFollowBtn && (
              <button
                className={`follow-overlay-btn ${isFollowingAuthor ? 'following' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFollow();
                }}
                title={isFollowingAuthor ? 'Unfollow' : 'Follow'}
              >
                {isFollowingAuthor ? <FaMinus /> : <FaPlus />}
              </button>
            )}
          </div>
          <div className="author-info">
            <div className="author-info-top">
              <div className="author-name" onClick={handleUsernameClick}>
                {formatName({ displayName: post.postedBy.displayName, username: post.postedBy.username })}
              </div>
              <CreatorRankBadge username={post.postedBy.username} />
              <div className="post-time">
                {formatRelativeTime(post.createdAt)}
                {/* "· edited" indicator (#292). Only lights up when
                    the server's updatedAt is more than ~2s after
                    createdAt — the INSERT sets both to NOW() in the
                    same statement, and small clock skew shouldn't
                    fake an edit for a fresh post. Uses HintTooltip
                    (not the native `title` attribute) so the hover
                    is instant + styled + touch-friendly. */}
                {(() => {
                  if (!post.updatedAt) return null;
                  const createdMs = Date.parse(post.createdAt);
                  const updatedMs = Date.parse(post.updatedAt);
                  if (!Number.isFinite(createdMs) || !Number.isFinite(updatedMs)) return null;
                  if (updatedMs - createdMs < 2000) return null;
                  return (
                    <HintTooltip
                      text={`Edited ${formatRelativeTime(post.updatedAt)}`}
                      placement="top"
                      className="post-edited-indicator"
                      icon={<span>· edited</span>}
                    />
                  );
                })()}
              </div>
            </div>
            {post.location && (
              <div className="post-location"><FaMapLocationDot/> {post.location}</div>
            )}
          </div>
        </div>
        <div className="post-header-right">
          {post.spotlight && new Date(post.spotlight.endTime) > new Date() && (
            <div className="spotlight-badge">
              ⭐ Spotlighted
              <SpotlightCountdown
                endTime={post.spotlight.endTime}
                className="spotlight-badge__countdown"
                showIcon={false}
              />
            </div>
          )}
          {/* Icon row: sits below the spotlight badge (column parent),
              but the two icon buttons themselves are side-by-side. */}
          <div className="post-header-icons">
            {/* Owner-only Edit trigger (#292). Opens a modal with
                PostComposer pre-filled in edit mode. */}
            {isOwner && (
              <button
                type="button"
                className="open-post-btn"
                onClick={() => setShowEditModal(true)}
                aria-label="Edit post"
                title="Edit post"
              >
                <FaPen />
              </button>
            )}
            {!isOnPostDetail && (
              <button
                type="button"
                className="open-post-btn"
                onClick={() => navigate(`/post/${post.id}`)}
                aria-label="Open post"
                title="Open post"
              >
                <FaArrowUpRightFromSquare />
              </button>
            )}
            {/* Report / Block the post author. Skipped for the author
                themselves (no self-reporting) and for anon (they can
                still see the affordance would exist, but clicking any
                action fires signup-guard). #371 safety table-stakes. */}
            {currentUserId && currentUserId !== post.postedBy.userId && (
              <UserActionsMenu
                targetUserId={post.postedBy.userId}
                targetName={formatName({
                  displayName: post.postedBy.displayName,
                  username: post.postedBy.username,
                })}
              />
            )}
          </div>
        </div>
      </div>

      {/* Post Content
          PPV preview modes (#257):
            - 'text_only' lets the full body through unlocked; the
              server keeps post.body populated and clears mediaUrl.
              Render the body the normal way and let the locked
              overlay sit under it.
            - 'char_limit' clears body, previewText carries the
              teaser. Render previewText like before.
            - 'none' clears both body and previewText. Just the lock.
          For any non-PPV locked post (e.g. paid content), the old
          previewText flow still applies — same as before this
          change. */}
      {isLocked ? (
        <div className="post-body post-body--locked">
          {post.previewMode === 'text_only' && post.body ? (
            <PostBodyRenderer body={post.body} />
          ) : (
            post.previewText && (
              <p className="post-preview-text">{post.previewText}</p>
            )
          )}
          {post.hasMedia && (
              <div className="locked-media-hint">🖼 Image(s) Locked</div>
            )}
          <div className="locked-overlay">
            <span className="locked-icon">🔒</span>
            <span className="locked-label">
              {post.viewType === 'ppv'
                ? `Unlock for ${post.ppvPrice} ${post.ppvTokenId || 'KLV'}`
                : 'Paid content'}
            </span>
            <button className="btn btn-primary btn-sm unlock-btn" onClick={handleUnlockClick}>
              {post.viewType === 'ppv' ? 'Unlock' : 'Subscribe'}
            </button>
          </div>
        </div>
      ) : (
        <div className="post-body">
          <PostBodyRenderer body={unlockedBody ?? post.body} />
        </div>
      )}

      {/* Post Media */}
      {!isLocked && (unlockedMediaUrl ?? post.mediaUrl) && (
        <div className="post-media">
          <img src={getMediaUrl((unlockedMediaUrl ?? post.mediaUrl)!) || undefined} alt="Post media" />
        </div>
      )}

      {/* Post Actions. Render the local interaction mirror so the
          card reflects the user's tap immediately regardless of which
          parent (Redux feed vs UserPostsFeed's local state) owns the
          post. */}
      {isLocked ? (
        // Show all engagement counts even when the body is locked —
        // hiding downvotes-only leaks a bias signal and prospective
        // subscribers should be able to see the full picture before
        // paying. Content stays gated, counts don't (issue #377).
        <div className="post-actions post-actions--locked">
          <span className="stat-readonly">👍 {interaction.likes}</span>
          <span className="stat-readonly">👎 {interaction.dislikes}</span>
          <span className="stat-readonly">💬 {interaction.comments}</span>
          {post.isAiGenerated && (
            <span
              className="ai-generated-badge"
              title="The creator marked this post as AI-generated"
            >
              🤖 AI-generated
            </span>
          )}
        </div>
      ) : (
        <div className="post-actions">
          <button
            className={`action-btn ${interaction.liked ? 'active' : ''}`}
            onClick={handleLike}
          >
            👍 {interaction.likes}
          </button>
          <button
            className={`action-btn ${interaction.disliked ? 'active' : ''}`}
            onClick={handleDislike}
          >
            👎 {interaction.dislikes}
          </button>
          <button
            className={`action-btn ${showComments ? 'active' : ''}`}
            onClick={handleComment}
          >
            💬 {interaction.comments}
          </button>

          {/* Spotlight Button - only show if user owns/posted and post is not already spotlighted */}
          {canSpotlight && !post.spotlight && (
            <PostSpotlightButton
              postId={post.id}
              durationHours={24}
              onSuccess={(duration) => {
                setCompletedDuration(duration);
                setShowSuccessModal(true);
                dispatch(updatePostSpotlight({ postId: post.id, durationHours: duration }));
                dispatch(updatePostSliceSpotlight({ postId: post.id, durationHours: duration }));
              }}
              className="spotlight-btn-sm"
            />
          )}

          {/* Creator-declared AI badge (#531). Moved from the header
              on 2026-08-07 — on mobile it was competing with the
              username for space. Bottom-right of the actions row keeps
              the disclosure visible without shrinking identity. */}
          {post.isAiGenerated && (
            <span
              className="ai-generated-badge"
              title="The creator marked this post as AI-generated"
            >
              🤖 AI-generated
            </span>
          )}
        </div>
      )}

      {/* Comments Section */}
      {showComments && (
        <div className="post-comments-section">
          <CommentList
            postId={post.id}
            currentUserId={currentUserId}
            onFetchComments={handleFetchComments}
            onCreateComment={handleCreateComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            followingUserIds={followingUserIds}
            onFollowToggle={(_userId, _isNowFollowing) => {
              // Redux state is automatically updated by the follow toggle
              // isFollowingAuthor will auto-update via useMemo when Redux state changes
            }}
          />
        </div>
      )}

      {/* Success Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <SpotlightSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title="Post Spotlighted!"
          message={`Post is now featured for ${completedDuration} hours! 🎉`}
          duration={5000}
        />,
        document.body
      )}

      {/* Edit Post Modal (#292) — portal so the modal escapes the
          card's stacking context. Composer runs in `initial` mode so
          on-chain-immutable fields stay disabled. */}
      {showEditModal && ReactDOM.createPortal(
        <div className="post-edit-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="post-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="post-edit-modal__header">
              <h3>Edit post</h3>
              <button
                type="button"
                className="post-edit-modal__close"
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <PostComposer
              submitLabel="Save changes"
              profileId={post.profileId ?? undefined}
              initial={{
                body: post.body,
                mediaUrl: post.mediaUrl,
                viewType: post.viewType,
                ppvPrice: post.ppvPrice,
                previewMode: post.previewMode ?? undefined,
                previewCharLimit: post.previewCharLimit ?? undefined,
                isAiGenerated: !!post.isAiGenerated,
              }}
              onSubmit={async (data) => {
                try {
                  await dispatch(editPost({
                    postId: post.id,
                    body: data.body,
                    mediaUrl: data.mediaUrl ?? null,
                    previewMode: data.previewMode,
                    previewCharLimit: data.previewCharLimit,
                    isAiGenerated: data.isAiGenerated,
                  })).unwrap();
                  toast.success('Post updated');
                  setShowEditModal(false);
                } catch (err: any) {
                  toast.error(typeof err === 'string' ? err : 'Failed to update post');
                }
              }}
            />
          </div>
        </div>,
        document.body,
      )}

      {/* Paywall Modal */}
      {showPaywall && (post.viewType === 'ppv' || post.viewType === 'paid' || post.isLocked) && (
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          onUnlocked={handleUnlocked}
          viewType={(post.viewType === 'ppv' ? 'ppv' : 'paid')}
          postId={post.id}
          profileId={post.profileId ?? 0}
          ppvPrice={post.ppvPrice}
          ppvTokenId={post.ppvTokenId}
          viewFee={viewFee}
          subscriptionFee={subscriptionFee}
          subscriptionPeriodSeconds={subscriptionPeriodSeconds}
          creatorName={formatName({ displayName: post.postedBy.displayName, username: post.postedBy.username })}
        />
      )}
    </SpotlightedPost>
  );
};

export default PostCard;
